import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PageHero from '../components/PageHero';
import Funicular from '../images/Sa Pa Funicular Train.jpg';
import PrincipalSponsors from '../images/dresscode/Principal Sponsors.png';
import MaleGuests from '../images/dresscode/Male Guests.png';
import FemaleGuest from '../images/dresscode/Female Guests.png';
import EntourageBoard from '../images/dresscode/Entourage Board.png';
import './pages.css';
import './Details.css';

const weddingDetails = {
  date: 'April 25, 2026',
  time: '1:30 PM',
  ceremony: {
    venue: 'Padre Pio Chapel',
    address: 'Las Villas De Dadiangas Subdivision, Purok 29, General Santos City, 9500',
    mapsLink: 'https://maps.app.goo.gl/CTYcE2vM3p6cTPgs6',
  },
  reception: {
    venue: 'Greenleaf Hotel Gensan',
    address: 'General Santos City',
    mapsLink: 'https://maps.app.goo.gl/a5y9W3nGRP9eG6Jf9',
  },
  schedule: [
    { time: '1:00 PM', event: 'Guest Arrival' },
    { time: '1:30 PM', event: 'Ceremony', highlight: true },
    { time: '3:00 PM', event: 'Cocktail Hour' },
    { time: '5:00 PM', event: 'Reception Dinner' },
    { time: '8:00 PM', event: 'Dancing' },
  ],
  notes: [
    'Parking at the ceremony venue is limited as it is located within a subdivision. Additional parking can be found around the surrounding area — please allow extra time.',
    'This is a strictly intimate celebration. No plus-ones and no children, please.',
  ],
  ceremonyEtiquette: {
    dos: [
      'Arrive on time — the ceremony begins promptly at 1:30 PM.',
      'Participate actively: stand, sit, and respond during the mass.',
      'Sing along during the hymns.',
      'Set your phones to silent before entering the chapel.',
    ],
    donts: [
      'Please do not talk or whisper during the ceremony.',
      'Avoid using flash photography during the mass.',
      'Do not leave your seat during the Consecration.',
      'Please refrain from filming the entire ceremony — be present with us.',
    ],
  },
  attire: [
    {
      group: 'Principal Sponsors',
      subtitle: 'Ninong & Ninang',
      lines: [
        'Ninong — Barong Tagalog with black dress pants or slacks.',
        'Ninang — Filipiniana or a beige formal long gown.',
      ],
      image: PrincipalSponsors,
      alt: 'Principal sponsors attire reference',
    },
    {
      group: 'Guests — Gentlemen',
      subtitle: 'Semi-Formal',
      lines: [
        'Please dress in semi-formal attire befitting the solemnity and elegance of the occasion.',
        'We kindly request that guests refrain from wearing jeans.',
      ],
      image: MaleGuests,
      alt: 'Male guest attire reference',
    },
    {
      group: 'Guests — Ladies',
      subtitle: 'Semi-Formal',
      lines: [
        'Please dress in semi-formal attire befitting the solemnity and elegance of the occasion.',
        'We kindly request that guests refrain from wearing jeans and white or black dresses.',
      ],
      image: FemaleGuest,
      alt: 'Female guest attire reference',
    },
  ],
};

const Details = () => {
  const [zoomedImg, setZoomedImg] = useState(null); // { src, alt }

  useEffect(() => {
    if (!zoomedImg) return;
    const onKey = (e) => { if (e.key === 'Escape') setZoomedImg(null); };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [zoomedImg]);

  const lightbox = zoomedImg ? createPortal(
    <div className="details-lightbox" onClick={() => setZoomedImg(null)}>
      <button className="details-lightbox-close" onClick={() => setZoomedImg(null)} aria-label="Close">✕</button>
      <img
        src={zoomedImg.src}
        alt={zoomedImg.alt}
        className="details-lightbox-img"
        onClick={e => e.stopPropagation()}
      />
    </div>,
    document.body
  ) : null;

  return (
    <>
    <div className="page" style={{ backgroundImage: `linear-gradient(rgba(22,23,25,0.6), rgba(22,23,25,0.6)), url(${Funicular})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'scroll' }}>
      <PageHero title="The Details" image={Funicular} />
      <div className="page-inner">

        <div className="details-grid">

          {/* Date & Time */}
          <div className="details-card">
            <p className="details-card-label">Date & Time</p>
            <p className="details-card-value">{weddingDetails.date}</p>
            <p className="details-card-sub details-card-sub--highlight">{weddingDetails.time}</p>
          </div>

          {/* Ceremony */}
          <div className="details-card details-card--wide">
            <p className="details-card-label">Ceremony</p>
            <p className="details-card-value">{weddingDetails.ceremony.venue}</p>
            <p className="details-card-sub">{weddingDetails.ceremony.address}</p>
            <a href={weddingDetails.ceremony.mapsLink} target="_blank" rel="noreferrer" className="details-map-link">
              View on Maps →
            </a>
          </div>

          {/* Reception */}
          <div className="details-card details-card--wide">
            <p className="details-card-label">Reception</p>
            <p className="details-card-value">{weddingDetails.reception.venue}</p>
            <p className="details-card-sub">{weddingDetails.reception.address}</p>
            <a href={weddingDetails.reception.mapsLink} target="_blank" rel="noreferrer" className="details-map-link">
              View on Maps →
            </a>
          </div>

          {/* Schedule */}
          <div className="details-card details-card--full">
            <p className="details-card-label">Schedule</p>
            <div className="details-schedule">
              {weddingDetails.schedule.map((item, index) => (
                <div key={index} className={`details-schedule-item${item.highlight ? ' details-schedule-item--highlight' : ''}`}>
                  <span className="details-schedule-time">{item.time}</span>
                  <span className="details-schedule-event">{item.event}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {weddingDetails.notes && (
            <div className="details-card details-card--full">
              <p className="details-card-label">Notes</p>
              {weddingDetails.notes.map((note, index) => (
                <p key={index} className="details-note">{note}</p>
              ))}
            </div>
          )}

          {/* Ceremony Etiquette */}
          <div className="details-card details-card--full">
            <p className="details-card-label">During the Ceremony</p>
            <div className="details-etiquette">
              <div className="details-etiquette-col">
                <p className="details-etiquette-heading">Please Do</p>
                {weddingDetails.ceremonyEtiquette.dos.map((item, i) => (
                  <p key={i} className="details-etiquette-item details-etiquette-item--do">{item}</p>
                ))}
              </div>
              <div className="details-etiquette-col">
                <p className="details-etiquette-heading">Please Don't</p>
                {weddingDetails.ceremonyEtiquette.donts.map((item, i) => (
                  <p key={i} className="details-etiquette-item details-etiquette-item--dont">{item}</p>
                ))}
              </div>
            </div>
          </div>

          {/* Dress Code */}
          <div className="details-card details-card--full">
            <p className="details-card-label">Dress Code</p>

            <button
              className="details-attire-full-wrap"
              onClick={() => setZoomedImg({ src: EntourageBoard, alt: 'Full attire reference — Bride, Groom, Ninong, Ninang, and Guests' })}
              aria-label="View full attire board"
            >
              <img src={EntourageBoard} alt="Full attire reference — Bride, Groom, Ninong, Ninang, and Guests" loading="lazy" />
            </button>

            <div className="details-attire-grid">
              {weddingDetails.attire.map((item) => (
                <button
                  key={item.group}
                  className="details-attire-card"
                  onClick={() => setZoomedImg({ src: item.image, alt: item.alt })}
                  aria-label={`View ${item.group} attire reference`}
                >
                  <div className="details-attire-img-wrap">
                    <img src={item.image} alt={item.alt} loading="lazy" />
                  </div>
                  <div className="details-attire-body">
                    <p className="details-attire-group">{item.group}</p>
                    <p className="details-attire-subtitle">{item.subtitle}</p>
                    {item.lines.map((line, i) => (
                      <p key={i} className="details-attire-line">{line}</p>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
    {lightbox}
    </>
  );
};

export default Details;
